import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Triangle } from "lucide-react";
import { ackLevelUp, type PendingLevelUp } from "../api/profile";
import { Button } from "./Button";

// ─── LevelUpOverlay ─────────────────────────────────────────────────────────────
// Full-screen game-style fanfare shown when GET /profile/stats reports a
// pendingLevelUp (level > celebrated_level). The medallion ticks up through
// each level crossed, then a gold burst reveals the Waypoints awarded.
// Dismissing acks via POST /profile/level-up/ack so it fires once per level,
// across devices. Mounted by AppShell; an internal dismissedTo guard hides it
// instantly while the ack + profile/stats refetch are in flight.

const CSS = `
.atlas-lvlup{
  position:fixed; inset:0; z-index:1000;
  display:grid; place-items:center; padding:24px;
  background:color-mix(in srgb, var(--bg-app) 72%, transparent);
  backdrop-filter:blur(7px); -webkit-backdrop-filter:blur(7px);
  animation:atlas-lvlup-fade .35s var(--ease-out);
}
@keyframes atlas-lvlup-fade{ from{ opacity:0; } }

.atlas-lvlup__card{
  position:relative; width:min(420px, 100%); overflow:hidden;
  display:flex; flex-direction:column; align-items:center; text-align:center;
  gap:6px; padding:44px 32px 30px;
  background:linear-gradient(160deg, var(--surface-raised), var(--surface-sunken));
  border:1px solid var(--border-gold); border-radius:var(--radius-lg);
  box-shadow:var(--glow-gold-sm), var(--shadow-lg, 0 24px 60px rgba(0,0,0,.5)), var(--ring-top);
  animation:atlas-lvlup-card .45s var(--ease-out);
}
@keyframes atlas-lvlup-card{ from{ opacity:0; transform:translateY(14px) scale(.96); } }

/* Slow-turning rays behind the medallion */
.atlas-lvlup__rays{
  position:absolute; top:-140px; left:50%; width:480px; height:480px;
  margin-left:-240px; pointer-events:none; opacity:.5;
  background:repeating-conic-gradient(
    from 0deg,
    color-mix(in srgb, var(--gold-400) 14%, transparent) 0deg 9deg,
    transparent 9deg 30deg);
  -webkit-mask-image:radial-gradient(closest-side, black 0%, transparent 72%);
  mask-image:radial-gradient(closest-side, black 0%, transparent 72%);
  animation:atlas-lvlup-spin 26s linear infinite;
}
@keyframes atlas-lvlup-spin{ to{ transform:rotate(360deg); } }

.atlas-lvlup__medal{
  position:relative; width:108px; height:108px; margin-bottom:14px;
  display:flex; flex-direction:column; align-items:center; justify-content:center;
  border-radius:50%; background:var(--accent); color:var(--text-on-gold);
  border:3px solid color-mix(in srgb, var(--gold-300) 70%, transparent);
  box-shadow:var(--glow-gold-sm), 0 0 44px color-mix(in srgb, var(--gold-400) 45%, transparent);
}
.atlas-lvlup__num{
  font-family:var(--font-display); font-weight:700; font-size:46px; line-height:1;
  font-variant-numeric:tabular-nums;
}
.atlas-lvlup__num--tick{ animation:atlas-lvlup-pop .5s var(--ease-out); }
@keyframes atlas-lvlup-pop{
  0%{ transform:scale(1.55); filter:brightness(1.6); }
  100%{ transform:scale(1); filter:none; }
}
.atlas-lvlup__medallbl{
  font-family:var(--font-mono); font-size:9px; text-transform:uppercase;
  letter-spacing:.16em; opacity:.8; margin-top:3px;
}

.atlas-lvlup__title{
  font-family:var(--font-display); font-weight:700; font-size:30px;
  letter-spacing:-.01em; line-height:1.1; margin:0;
}
.atlas-lvlup__sub{ font-size:13.5px; color:var(--text-secondary); margin:0; }

.atlas-lvlup__reveal{ animation:atlas-lvlup-rise .5s var(--ease-out) both; }
@keyframes atlas-lvlup-rise{ from{ opacity:0; transform:translateY(10px); } }

.atlas-lvlup__reward{
  display:flex; align-items:center; justify-content:center; gap:9px;
  width:100%; margin-top:16px; padding:13px 16px;
  border-radius:var(--radius-md); background:var(--surface-sunken);
  border:1px solid var(--border-gold);
}
.atlas-lvlup__rewardval{
  font-family:var(--font-display); font-weight:700; font-size:20px;
  font-variant-numeric:tabular-nums; color:var(--gold-400);
}
.atlas-lvlup__rewardlbl{
  font-family:var(--font-mono); font-size:10px; text-transform:uppercase;
  letter-spacing:.14em; color:var(--text-muted);
}

.atlas-lvlup__actions{
  display:flex; flex-direction:column; align-items:stretch; gap:8px;
  width:100%; margin-top:18px;
}

/* Gold shards bursting from the medallion once the final level lands */
.atlas-lvlup__shard{
  position:absolute; top:88px; left:50%; width:7px; height:7px;
  border-radius:1.5px; background:var(--gold-400); pointer-events:none;
  opacity:0; animation:atlas-lvlup-burst 1s var(--ease-out) both;
}
@keyframes atlas-lvlup-burst{
  0%{ opacity:0; transform:translate(0,0) rotate(0) scale(.5); }
  12%{ opacity:1; }
  100%{ opacity:0; transform:translate(var(--dx), var(--dy)) rotate(var(--rot)) scale(1); }
}

@media (prefers-reduced-motion: reduce){
  .atlas-lvlup, .atlas-lvlup__card, .atlas-lvlup__num--tick,
  .atlas-lvlup__reveal, .atlas-lvlup__shard{ animation:none; }
  .atlas-lvlup__rays{ animation:none; }
}
`;

function ensure() {
  if (typeof document === "undefined") return;
  if (!document.getElementById("atlas-lvlup-css")) {
    const s = document.createElement("style");
    s.id = "atlas-lvlup-css";
    s.textContent = CSS;
    document.head.appendChild(s);
  }
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function LevelUpOverlay({ pending }: { pending: PendingLevelUp | null }) {
  ensure();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Levels acked this session — hides the overlay immediately on dismiss and
  // keeps it hidden if profile/stats refetches before the ack lands.
  const [dismissedTo, setDismissedTo] = React.useState(0);

  const active = pending && pending.toLevel > dismissedTo ? pending : null;

  const ackMutation = useMutation({
    mutationFn: ackLevelUp,
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ["profile/stats"] });
    },
  });

  if (!active) return null;

  const dismiss = () => {
    setDismissedTo(active.toLevel);
    ackMutation.mutate();
  };

  return (
    <LevelUpCard
      key={active.toLevel}
      pending={active}
      onDismiss={dismiss}
      onStore={() => {
        dismiss();
        void navigate({ to: "/store" });
      }}
    />
  );
}

function LevelUpCard({
  pending,
  onDismiss,
  onStore,
}: {
  pending: PendingLevelUp;
  onDismiss: () => void;
  onStore: () => void;
}) {
  const reduced = prefersReducedMotion();
  const [displayLevel, setDisplayLevel] = React.useState(
    reduced ? pending.toLevel : pending.fromLevel,
  );
  const [landed, setLanded] = React.useState(reduced);

  // Tick the medallion up through every level crossed, then reveal rewards.
  React.useEffect(() => {
    if (reduced) return;
    const steps = pending.toLevel - pending.fromLevel;
    const stepMs = Math.min(550, Math.max(180, 1100 / steps));
    let i = 0;
    let interval: ReturnType<typeof setInterval> | undefined;
    const start = setTimeout(() => {
      interval = setInterval(() => {
        i++;
        setDisplayLevel(pending.fromLevel + i);
        if (i >= steps) {
          if (interval) clearInterval(interval);
          setLanded(true);
        }
      }, stepMs);
    }, 650);
    return () => {
      clearTimeout(start);
      if (interval) clearInterval(interval);
    };
  }, [pending.fromLevel, pending.toLevel, reduced]);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const shards = React.useMemo(() => {
    if (!landed || reduced) return [];
    return Array.from({ length: 16 }, (_, i) => {
      const angle = (i / 16) * Math.PI * 2 + 0.35;
      const dist = 120 + (i % 4) * 26;
      return {
        dx: `${Math.round(Math.cos(angle) * dist)}px`,
        dy: `${Math.round(Math.sin(angle) * dist * 0.85)}px`,
        rot: `${(i % 2 === 0 ? 1 : -1) * (140 + i * 17)}deg`,
        delay: `${(i % 5) * 45}ms`,
      };
    });
  }, [landed, reduced]);

  return (
    <div
      className="atlas-lvlup"
      role="dialog"
      aria-modal="true"
      aria-label={`Level up! You reached level ${pending.toLevel}`}
      onClick={onDismiss}
    >
      <div className="atlas-lvlup__card" onClick={(e) => e.stopPropagation()}>
        <div className="atlas-lvlup__rays" aria-hidden="true" />
        {shards.map((s, i) => (
          <span
            key={i}
            className="atlas-lvlup__shard"
            aria-hidden="true"
            style={
              {
                "--dx": s.dx,
                "--dy": s.dy,
                "--rot": s.rot,
                animationDelay: s.delay,
              } as React.CSSProperties
            }
          />
        ))}

        <div className="atlas-lvlup__medal">
          <span
            key={displayLevel}
            className={`atlas-lvlup__num${displayLevel > pending.fromLevel ? " atlas-lvlup__num--tick" : ""}`}
          >
            {displayLevel}
          </span>
          <span className="atlas-lvlup__medallbl">Level</span>
        </div>

        <span className="eyebrow" style={{ fontSize: 10, color: "var(--gold-400)" }}>
          Outdoor Score milestone
        </span>
        <h2 className="atlas-gold-text atlas-lvlup__title">Level up!</h2>
        <p className="atlas-lvlup__sub">
          {pending.toLevel - pending.fromLevel > 1
            ? `You climbed from level ${pending.fromLevel} to level ${pending.toLevel}.`
            : `You reached level ${pending.toLevel}. Keep exploring.`}
        </p>

        {landed && (
          <>
            <div className="atlas-lvlup__reveal atlas-lvlup__reward">
              <Triangle
                size={13}
                fill="currentColor"
                aria-hidden="true"
                style={{ color: "var(--gold-400)", flexShrink: 0 }}
              />
              <span className="atlas-lvlup__rewardval">
                +{pending.waypointsAwarded.toLocaleString("en-GB")}
              </span>
              <span className="atlas-lvlup__rewardlbl">Waypoints awarded</span>
            </div>

            <div className="atlas-lvlup__reveal atlas-lvlup__actions" style={{ animationDelay: "120ms" }}>
              <Button autoFocus variant="primary" block onClick={onDismiss}>
                Continue exploring
              </Button>
              <Button variant="ghost" block onClick={onStore}>
                Spend Waypoints in the store
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
